import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../AuthContext';

console.log('SignInCheck: File loaded');

function SignInCheck() {
  const { user, ready, supabase, checkAuthStatus, fetchCsrfToken, profile, isInitializing } = useAuth(); // UPDATED: Add 'profile' and 'isInitializing' from AuthContext
  const [checking, setChecking] = useState(true);
  const [localProfile, setLocalProfile] = useState(null);
  const navigate = useNavigate();

  console.log('SignInCheck: Component rendering...');

  // Effect 1: Wait for AuthContext, ensure user exists, then fetch profile (GUARDED)
  useEffect(() => {
    console.log('[Effect 1] Running. AuthContext Ready:', ready, 'User Object Exists:', !!user);
    if (!ready) {
      console.log('[Effect 1] AuthContext not ready yet, waiting...');
      return;
    }
    if (!user) {
      console.log('[Effect 1] AuthContext is ready, but user object is not yet available. Setting timeout to recheck...');
      const timer = setTimeout(() => {
        if (!user) { // Check the prop again
          console.log('[Effect 1 - Timeout] User still not found after delay. Redirecting to signin.');
          navigate('/signin', { replace: true });
        } else {
          console.log('[Effect 1 - Timeout] User object appeared after delay.');
        }
      }, 750);
      return () => clearTimeout(timer);
    }
    // NEW: Guard - Skip fetch if AuthContext already loaded profile (avoids duplicate RPC)
    if (profile) {
      console.log('[Effect 1] Profile already loaded from AuthContext - syncing to local and done.');
      setLocalProfile(profile);
      setChecking(false);
      return;
    }
    console.log('[Effect 1] Ready and User exists! Fetching profile for user ID:', user.id);
    (async () => {
      // Don't reset checking here
      let profData = null; // Variable to hold data outside try block
      let error = null; // Variable to hold error outside try block
      try {
        const result = await supabase
          .schema('clients')
          .rpc('select_profile', { p_client_id: user.id });
        // *** ADDED LOG ***
        // Log immediately after the await to see if it resolves
        console.log('[Effect 1] select_profile RPC call FINISHED. Result:', result);
        // *** END ADDED LOG ***
        profData = result.data;
        error = result.error;
        if (error) throw error; // Throw if Supabase returned an error object
        console.log('[Effect 1] Profile fetch SUCCESS. Raw Data:', profData);
        setLocalProfile(profData?.[0] || null);
      } catch (err) {
        console.error('[Effect 1] Profile fetch CATCH block error:', err.message);
        setLocalProfile(null);
      } finally {
        console.log('[Effect 1] FINALLY block: Setting checking to false.');
        setChecking(false);
      }
    })();
  }, [user, ready, supabase, navigate, profile]); // UPDATED DEPS: Add 'profile' (triggers skip on change)

  // Effect 2: Route the user based on profile check result
  useEffect(() => {
    console.log('[Effect 2] Running. Profile Checking complete:', !checking, 'User exists:', !!user, 'LocalProfile exists:', !!localProfile);
    if (checking) {
      console.log('[Effect 2] Still checking profile (Effect 1 running), waiting...');
      return;
    }
    // --- Routing Logic ---
    if (user && localProfile) {
      if (localProfile.client_id === user.id) {
        const returnTo = localStorage.getItem('returnTo') || sessionStorage.getItem('returnTo');
        if (returnTo) {
          console.log('[Effect 2] User and matching profile found. Routing to returnTo:', returnTo);
          localStorage.removeItem('returnTo');
          sessionStorage.removeItem('returnTo');
          navigate(returnTo, { replace: true });
          return;
        }
        console.log('[Effect 2] User and matching profile found. Routing to pipeline...');
        navigate('/estate-sales-pipeline', { replace: true });
        return;
      } else {
        console.error('[Effect 2] Profile found but client_id mismatch. Redirecting to signin.');
        navigate('/signin', { replace: true });
        return;
      }
    }
    if (user && !localProfile) {
      console.log('[Effect 2] User found but profile NOT found. Routing to complete register...');
      navigate('/complete-register', {
        replace: true,
        state: { userId: user.id, metadata: user.user_metadata || {} }
      });
      return;
    }
    if (!user) {
      console.log('[Effect 2] Fallback: No user object found after checks. Redirecting to signin.');
      navigate('/signin', { replace: true });
      return;
    }
    console.warn('[Effect 2] Unexpected state reached. Redirecting to signin as fallback.');
    navigate('/signin', { replace: true });
  }, [checking, user, localProfile, navigate]); // FIXED DEPS: Remove unused 'checkAuthStatus, fetchCsrfToken'

  console.log('SignInCheck: Rendering UI (Verifying Status)...');
  // UPDATED: UI - Hold spinner longer with isInitializing for full AuthContext settle (e.g., RPC lag)
  if (checking || isInitializing || !ready) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center p-6 bg-white rounded-lg shadow-md">
          <p className="text-lg text-gray-700">Verifying your status...</p>
          <div className="mt-4 animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
        </div>
      </div>
    );
  }

  // NEW: Fallback render if not redirecting (shouldn't hit, but prevents blank)
  return <div className="text-center p-6">Redirecting...</div>;
}

export default SignInCheck;
